/**
 * Population pyramids: reading age–sex data, turning counts into shares, and the summary
 * figures a geography course asks for (age bands, dependency ratio, sex ratio, median age).
 *
 * Data comes as `age group, males, females` lines, youngest or oldest first. Five-year
 * cohorts (0–4, 5–9 … 85+) are the standard, but any bands work for the picture; the
 * age-band figures need labels that start with a number so the groups can be placed.
 */

import { parseLabeledRows, seriesNames } from "@/lib/tool-kit/charts/parse";

export interface AgeGroup {
  label: string;
  male: number;
  female: number;
  /** The first age in the group (the 5 of "5–9"), or null when the label has no leading number. */
  start: number | null;
}

export interface PyramidData {
  groups: AgeGroup[];
  /** Series names from a header line, when given. */
  maleName: string | null;
  femaleName: string | null;
  /** Line numbers with no usable numbers. */
  skipped: number[];
  /** Cells that were empty or not numbers and were counted as zero. */
  missingCells: number;
  /** Cells holding a negative number, drawn as their absolute value. */
  negativeCells: number;
  /** True when the groups were reordered youngest first. */
  reordered: boolean;
}

const LEADING_NUMBER = /^\s*(\d+)/;

export function ageStart(label: string): number | null {
  const match = LEADING_NUMBER.exec(label);
  return match ? Number(match[1]) : null;
}

/** Parse `age group, males, females` lines, sorting by age when every label starts with a number. */
export function parsePyramid(text: string): PyramidData {
  const table = parseLabeledRows(text);
  const names = seriesNames(table, () => "");
  let missingCells = 0;
  let negativeCells = 0;
  const groups: AgeGroup[] = table.rows.map((row) => {
    const read = (value: number | null) => {
      if (value === null) {
        missingCells += 1;
        return 0;
      }
      if (value < 0) {
        negativeCells += 1;
      }
      return Math.abs(value);
    };
    return {
      label: row.label.trim(),
      male: read(row.values[0] ?? null),
      female: read(row.values[1] ?? null),
      start: ageStart(row.label),
    };
  });
  const sortable =
    groups.length > 1 && groups.every((group) => group.start !== null);
  let reordered = false;
  if (sortable) {
    const sorted = [...groups].sort((a, b) => (a.start ?? 0) - (b.start ?? 0));
    reordered = sorted.some((group, index) => group !== groups[index]);
    groups.splice(0, groups.length, ...sorted);
  }
  return {
    groups,
    maleName: names[0] ? names[0] : null,
    femaleName: names[1] ? names[1] : null,
    skipped: table.skipped,
    missingCells,
    negativeCells,
    reordered,
  };
}

export type InputBasis = "counts" | "percent";
export type DisplayBasis = "counts" | "percent";

export function totalOf(groups: readonly AgeGroup[]): number {
  return groups.reduce((sum, group) => sum + group.male + group.female, 0);
}

/**
 * The values to draw, per sex. Counts shown as percent are divided by the whole
 * population; percentages are already shares and are drawn as given.
 */
export function displayValues(
  groups: readonly AgeGroup[],
  input: InputBasis,
  display: DisplayBasis
): { male: number[]; female: number[] } {
  const total = totalOf(groups);
  const scale =
    input === "counts" && display === "percent" && total > 0 ? 100 / total : 1;
  return {
    male: groups.map((group) => group.male * scale),
    female: groups.map((group) => group.female * scale),
  };
}

export interface AgeStats {
  /** Shares of the whole population, in percent. */
  under15: number;
  working: number;
  over65: number;
  /** (under 15 + 65 and over) per 100 people aged 15–64. Null with no working-age people. */
  dependencyRatio: number | null;
  youthRatio: number | null;
  oldAgeRatio: number | null;
  /** Males per 100 females. */
  sexRatio: number | null;
  /** By linear interpolation within the group that holds the 50 % point; null if unavailable. */
  medianAge: number | null;
}

/** Summary figures. Needs groups that start with an age; returns null without them. */
export function ageStats(groups: readonly AgeGroup[]): AgeStats | null {
  const total = totalOf(groups);
  if (total <= 0 || groups.some((group) => group.start === null)) {
    return null;
  }
  const share = (test: (start: number) => boolean) =>
    (groups
      .filter((group) => test(group.start ?? 0))
      .reduce((sum, group) => sum + group.male + group.female, 0) /
      total) *
    100;
  const under15 = share((start) => start < 15);
  const over65 = share((start) => start >= 65);
  const working = 100 - under15 - over65;
  const males = groups.reduce((sum, group) => sum + group.male, 0);
  const females = groups.reduce((sum, group) => sum + group.female, 0);
  return {
    under15,
    working,
    over65,
    dependencyRatio: working > 0 ? ((under15 + over65) / working) * 100 : null,
    youthRatio: working > 0 ? (under15 / working) * 100 : null,
    oldAgeRatio: working > 0 ? (over65 / working) * 100 : null,
    sexRatio: females > 0 ? (males / females) * 100 : null,
    medianAge: medianAge(groups, total),
  };
}

function medianAge(groups: readonly AgeGroup[], total: number): number | null {
  let before = 0;
  for (const [index, group] of groups.entries()) {
    const count = group.male + group.female;
    if (count > 0 && before + count >= total / 2) {
      const next = groups[index + 1]?.start;
      const start = group.start ?? 0;
      if (next === undefined || next === null) {
        return null;
      }
      return start + ((total / 2 - before) / count) * (next - start);
    }
    before += count;
  }
  return null;
}

/** The largest value drawn, for a symmetric axis. */
export function maxValue(sets: readonly (readonly number[])[]): number {
  return Math.max(0, ...sets.flat());
}
